import React, { forwardRef, useRef, useState, useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, Grid } from "@mui/material";
import { GradientColorHeader, ProductInfo, ReadTextField } from "@curves/ui-shared";
// TODO: 改為庫存與 icoach 共用的 EmployeeSelect 後統一匯入路徑
import { EmployeeSelect } from "@icoach/components";
import { Box, Button, Stack, Divider, DateTimeStandard } from "@curves/ui-base";
import { Typography } from "@curves/ui-base";
import { ExpectedQuantityCell, getExpectedQuantityCellClassName } from "@clubInventory/purchase/components/";
import { getNowTime, parseDate } from "@util/moment";
import { callRef, isEmpty, isFunction, refIsRequiredError } from "@util/utils";
import useGetEmployeeOptionState from "@util/hook/useGetEmployeeOptionState";
import { useSnackbar } from "notistack";
import useClubPurchaseReceiptApi from "@apis/useClubPurchaseReceiptApi";
import useClubInspectionOrderApi from "@apis/useClubInspectionOrderApi";
import { SkuAddToList, InventoryProductList, InputGroup } from "@clubInventory/components/";
import { usePurchaseInspectionSkuAdd } from "@clubInventory/hooks/useSkuAddToList";
import { getProductRowKey } from "@clubInventory/utils";

// 功能：驗收商品清單欄位定義（商品資訊、應驗收數量、原訂購數量）
const inspectionHeaderRow = [
	{
		cellKey: "productName",
		headerLabel: "商品清單",
		minWidth: 200,
		formatCell: (productName, row) => (
			<ProductInfo
				product={{
					name: productName,
					sku: row.sku,
					spec: row.variantName,
					imageUrl: row.pictureSrc,
				}}
			/>
		),
	},
	{
		cellKey: "expectedQuantity",
		className: "p-0",
		headerLabel: "應驗收",
		align: "center",
		minWidth: 100,
		getCellClassName: (expectedQuantity, row) => getExpectedQuantityCellClassName(row.actualQuantity, expectedQuantity),
		formatCell: (expectedQuantity, row, { onOpenQuantityEdit }) => (
			<ExpectedQuantityCell actualQuantity={row.actualQuantity} expectedQuantity={expectedQuantity} item={row} onOpenQuantityEdit={onOpenQuantityEdit} showCheckIcon />
		),
	},
	{
		cellKey: "orderQuantity",
		headerLabel: "訂購數量",
		align: "center",
		minWidth: 100,
		formatCell: orderQuantity => <Typography>{orderQuantity ?? 0}</Typography>,
	},
];

// 功能：送出 API payload 的 item 映射（前端列表 → 驗收 API 格式）
const toInspectionItem = row => ({
	productID: row.productID,
	variantID: row.variantID,
	quantity: row.orderQuantity || 0,
	completedQuantity: row.actualQuantity || 0,
	shouldInspectionQuantity: row.expectedQuantity || 0,
});

// 功能：進貨單明細排序——後端 Items 的 shouldInspectionQuantity 為「剩餘應驗數」(Quantity−Actual)，
// 剩餘為 0 表示該列已驗完（與應驗欄勾選／0 顯示一致），排到最後；其餘維持原順序
const sortReceiptItemsForInspection = items => {
	if (!Array.isArray(items) || items.length === 0) return items ?? [];
	return [...items].sort((a, b) => {
		const aDone = Number(a.shouldInspectionQuantity ?? 0) === 0;
		const bDone = Number(b.shouldInspectionQuantity ?? 0) === 0;
		if (aDone === bDone) return 0;
		return aDone ? 1 : -1;
	});
};

// 功能：驗收送出失敗時重抓單據，實收保留使用者輸入；應驗收以後端 shouldInspectionQuantity 為準（API 未回該欄才沿用清單舊值）
const mergeReceiptWithCurrentQuantities = (newItems, currentList) => {
	if (!Array.isArray(newItems)) return [];
	const currentMap = new Map(currentList.map(p => [getProductRowKey(p), p]));
	return newItems.map(item => {
		const cur = currentMap.get(getProductRowKey(item));
		const currentActualQuantity = cur ? (cur.actualQuantity ?? 0) : 0;
		// 如果應驗收數量為 0，則實收數量為 0，否則使用現有實收數量
		const actualQuantity = item.shouldInspectionQuantity === 0 ? 0 : currentActualQuantity;

		return {
			productID: item.productID,
			productName: item.productName,
			variantID: item.variantID,
			variantName: item.variantName,
			sku: item.sku,
			pictureSrc: item.pictureSrc,
			actualQuantity: actualQuantity,
			expectedQuantity: item.shouldInspectionQuantity ?? cur?.expectedQuantity ?? 1,
			orderQuantity: item.quantity ?? 0,
		};
	});
};

// 功能：進貨單驗收與入庫（掃碼驗收、填寫驗收人、送出入庫）
const PurchaseInspectionAndStorageDialog = forwardRef(({ open, onClose, sourceData, refresh, ...props }, ref) => {
	// 功能：欄位與服務
	const inspectorRef = useRef(null);
	const { employeeOption } = useGetEmployeeOptionState();
	const { enqueueSnackbar } = useSnackbar();
	const { getClubPurchaseReceiptApi } = useClubPurchaseReceiptApi();
	const { postClubInspectionOrderApi } = useClubInspectionOrderApi();

	// 功能：進貨單與商品列表來源（API 原始資料 + 重新載入時覆蓋用列表）
	const [receiptData, setReceiptData] = useState(null);
	const [initialProductsOverride, setInitialProductsOverride] = useState(null);

	const clubPurchaseReceiptID = sourceData?.clubPurchaseReceiptID;
	const receiptDataItems = receiptData?.items;
	const receiptItems = useMemo(() => sortReceiptItemsForInspection(receiptDataItems ?? []), [receiptDataItems]);

	// 功能：依進貨單編號抓取最新進貨單資料
	const fetchReceipt = useCallback(async () => {
		if (!clubPurchaseReceiptID) return null;
		const resp = await getClubPurchaseReceiptApi(clubPurchaseReceiptID);
		if (resp) setReceiptData(resp);
		return resp ?? null;
		// eslint-disable-next-line
	}, [clubPurchaseReceiptID]);

	// 功能：Dialog 開啟或來源變更時，重新載入進貨單資料並重置覆蓋列表
	useEffect(() => {
		if (!open || !clubPurchaseReceiptID) {
			setReceiptData(null);
			setInitialProductsOverride(null);
			return;
		}
		setInitialProductsOverride(null);
		fetchReceipt();
		// eslint-disable-next-line
	}, [open, clubPurchaseReceiptID, fetchReceipt]);

	// 功能：將進貨單 items 轉成驗收列表初始商品（驗收數量預設為 shouldInspectionQuantity）
	const initialProductsFromReceipt = useMemo(
		() =>
			receiptItems.map(item => ({
				productID: item.productID,
				productName: item.productName,
				variantID: item.variantID,
				variantName: item.variantName,
				sku: item.sku,
				pictureSrc: item.pictureSrc,
				actualQuantity: 0,
				expectedQuantity: item.shouldInspectionQuantity ?? 1,
				orderQuantity: item.quantity ?? 0,
			})),
		// eslint-disable-next-line
		[receiptItems],
	);

	// 功能：實際提供給驗收列表的初始商品（若有覆蓋則優先使用覆蓋版）
	const initialProducts = initialProductsOverride ?? initialProductsFromReceipt;

	// 功能：依 SKU 從進貨單中找出商品，只允許驗收進貨單內的品項
	const fetchProductBySkuFromReceipt = useCallback(
		async sku => {
			const normalized = sku?.trim();
			if (!normalized) return null;
			const match = receiptItems.find(item => item.sku === normalized);
			if (isEmpty(match)) {
				enqueueSnackbar("此商品未列於進貨單，請確認品項。", { variant: "error" });
				return null;
			}
			return {
				productID: match.productID,
				productName: match.productName,
				variantID: match.variantID,
				variantName: match.variantName,
				sku: match.sku,
				pictureSrc: match.pictureSrc,
				actualQuantity: 1,
				expectedQuantity: match.shouldInspectionQuantity ?? 1,
				orderQuantity: match.quantity ?? 0,
			};
		},
		// eslint-disable-next-line
		[receiptItems, enqueueSnackbar],
	);

	// 功能：掃碼/輸入 SKU 加入驗收商品列表，驗收版的 SKU Add（含實收數量邏輯）
	const skuAdd = usePurchaseInspectionSkuAdd({
		initialProducts,
		fetchProductBySku: fetchProductBySkuFromReceipt,
	});
	const { products: productList, clearProducts: clearProductList } = skuAdd;

	// 功能：關閉 Dialog 時清除商品列表及覆蓋狀態，並呼叫 onClose
	const handleClose = e => {
		setInitialProductsOverride(null);
		clearProductList();
		isFunction(onClose) && onClose(e);
	};

	// 功能：送出驗收與入庫
	const handleSubmit = async () => {
		if (refIsRequiredError(inspectorRef)) return;
		// 確認至少一筆實收數量 > 0
		const hasValidQuantity = productList.some(row => (row.actualQuantity || 0) > 0);
		if (!hasValidQuantity) {
			enqueueSnackbar("驗收數量不可全部為零，請至少填寫一筆數量。", { variant: "error" });
			return;
		}

		const employeeID = callRef(inspectorRef, "getResult");
		const payload = {
			clubPurchaseReceiptID,
			purchaseDate: getNowTime(),
			employeeID: employeeID != null ? Number(employeeID) : 0,
			items: productList.map(toInspectionItem),
			updatedDate: receiptData?.inspectionOrderUpdateDate,
		};

		try {
			const resp = await postClubInspectionOrderApi(payload);
			if (resp != null) {
				isFunction(refresh) && refresh();
				handleClose();
				return;
			}
			enqueueSnackbar("驗收單建立失敗，請稍後再試。", { variant: "error" });
			// 失敗時重抓單據並合併已輸入數量，避免使用者輸入流失
			const newReceipt = await fetchReceipt();
			if (newReceipt?.items?.length) {
				setInitialProductsOverride(mergeReceiptWithCurrentQuantities(newReceipt.items, productList));
			}
		} catch {
			const newReceipt = await fetchReceipt();
			if (newReceipt?.items?.length) {
				setInitialProductsOverride(mergeReceiptWithCurrentQuantities(newReceipt.items, productList));
			}
		}
	};

	return (
		<Dialog className="simple-content dialog" PaperProps={{ className: "wx-80rem" }} open={open} onClose={handleClose} fullWidth maxWidth="lg" {...props}>
			<GradientColorHeader onClose={handleClose}>進貨單驗收及入庫</GradientColorHeader>
			<DialogContent className="p-4">
				<Grid container spacing={3}>
					<Grid xs={4} item>
						<ReadTextField label="進貨單編號">{receiptData?.purchaseNo ?? ""}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<ReadTextField label="建單日期">{parseDate(getNowTime(), DateTimeStandard.DisplayDateBySlash)}</ReadTextField>
					</Grid>
					<Grid xs={4} item>
						<EmployeeSelect label="驗收人" ref={inspectorRef} options={employeeOption} fullWidth required />
					</Grid>
					<Grid xs={12} item>
						<Divider className="my-2" />
					</Grid>
					<Grid xs={12} item>
						<Box sx={{ borderTop: "1px dashed #ccc", pt: 3, mt: 1 }}>
							<SkuAddToList {...skuAdd}>
								<Stack direction="row" spacing={2} alignItems="center" className="mb-3">
									<InputGroup className="product-input-group" sx={{ flex: 1 }}>
										<InputGroup.StartAdornment>
											<SkuAddToList.ScanButton />
										</InputGroup.StartAdornment>
										<InputGroup.Input>
											<SkuAddToList.Input placeholder="請使用手持掃描器掃描或輸入商品SKU" />
										</InputGroup.Input>
									</InputGroup>
									<SkuAddToList.SubmitButton>加入商品</SkuAddToList.SubmitButton>
								</Stack>
								<SkuAddToList.ScannerDialog title="商品掃描" continuousScan />
								<SkuAddToList.ConfirmDialog />
								<SkuAddToList.List>
									<InventoryProductList headerRow={inspectionHeaderRow} emptyMessage="請掃描商品條碼進行驗收" height="406px" />
								</SkuAddToList.List>
							</SkuAddToList>
						</Box>
					</Grid>
				</Grid>
				<Box className="mt-3 text-right">
					<Button variant="contained" onClick={handleSubmit}>
						確認入庫
					</Button>
				</Box>
			</DialogContent>
		</Dialog>
	);
});

PurchaseInspectionAndStorageDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	sourceData: PropTypes.shape({ clubPurchaseReceiptID: PropTypes.number }),
	refresh: PropTypes.func,
};

PurchaseInspectionAndStorageDialog.displayName = "PurchaseInspectionAndStorageDialog";

export default PurchaseInspectionAndStorageDialog;
